import React from 'react'
import { ArtikeldatenFile, BarcodeAndSonderpfand, CrateFile, Modification, ShapeFile } from 'domain/DTO'
import { API_HOST } from 'lib'
import { BarcodeCommonTable } from './barcode/BarcodeCommonTable'
import { CrateInstallationTable } from './crate/CrateInstallationTable'
import { ArtikeldatenTable } from './artikeldaten/ArtikeldatenTable'
import { Config } from 'domain/Assortment'
import { RD, SRD } from 'srd'
import { HttpError } from '../../lib/RemoteData'
import { CrateCommonTable } from './crate/CrateCommonTable'
import { BarcodeInstallationTable } from './barcode/BarcodeInstallationTable'
import { ShapeCommonTable } from './shape/ShapeCommonTable'
import { ShapeInstallationTable } from './shape/ShapeInstallationTable'
import { LoadingSpinner } from '../LoadingSpinner'

export const paginationOptions = [10, 20, 50, 100]

type ShapeCommonTableProps = {
  config: Config
  shape: RD<HttpError, ShapeFile>
  common: RD<HttpError, Modification[]>
  commonId: string
  refresh: () => void
}

type ShapeInstallationTableProps = {
  config: Config
  shape: RD<HttpError, ShapeFile>
  common: RD<HttpError, Modification[]>
  override?: RD<HttpError, Modification[]>
  installationId: string
  refresh: () => void
}

export const ShapeCommonTableWrapper = ({ config, shape, common, commonId, refresh }: ShapeCommonTableProps) => {
  const combinedRemoteData = SRD.map2(
    (shape, common) => ({
      shape,
      common,
    }),
    shape,
    common,
  )
  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div className="alert alert-warning mb-md mt-md">
          No file seems to be uploaded to file stream
          <div>{err.body?.title}</div>
        </div>
      ),
      success: ({ shape, common }) => (
        <ShapeCommonTable
          config={config}
          database={shape}
          commonModifications={common}
          modificationUrl={`${API_HOST}/v1/common/${commonId}/SHAPE/modifications`}
          refreshModifications={refresh}
        />
      ),
    },
    combinedRemoteData,
  )
}

export const ShapeInstallationTableWrapper = ({
  config,
  shape,
  common,
  override,
  installationId,
  refresh,
}: ShapeInstallationTableProps) => {
  const combinedRemoteData = SRD.map2(
    (shape, common) => ({
      shape,
      common,
    }),
    shape,
    common,
  )
  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div className="alert alert-warning mb-md mt-md">
          No file seems to be uploaded to file stream
          <div>{err.body?.title}</div>
        </div>
      ),
      success: ({ shape, common }) => (
        <ShapeInstallationTable
          config={config}
          database={shape}
          commonModifications={common}
          installationModifications={override && SRD.isSuccess(override) ? override.data : []}
          modificationUrl={`${API_HOST}/v1/installation/${installationId}/SHAPE/modifications`}
          refreshModifications={refresh}
        />
      ),
    },
    combinedRemoteData,
  )
}

type BarcodeTableProps = {
  config: Config
  barcode: RD<HttpError, BarcodeAndSonderpfand>
  common: RD<HttpError, Modification[]>
  commonId: string
  refresh: () => void
}

type BarcodeInstallationTableProps = {
  config: Config
  barcode: RD<HttpError, BarcodeAndSonderpfand>
  common: RD<HttpError, Modification[]>
  override?: RD<HttpError, Modification[]>
  installationId?: string
  refresh: () => void
}

export const BarcodeCommonTableWrapper = ({ config, barcode, common, commonId, refresh }: BarcodeTableProps) => {
  const combinedRemoteData = SRD.map2((barcode, common) => ({ barcode, common }), barcode, common)

  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div>
          <div className="alert alert-warning mb-md mt-md">
            No file seems to be uploaded to file stream
            <div>{err.body?.title}</div>
          </div>
        </div>
      ),
      success: ({ barcode, common }) => (
        <BarcodeCommonTable
          config={config}
          database={barcode}
          commonId={commonId}
          commonModifications={common}
          modificationUrl={`${API_HOST}/v1/common/${commonId}/SHAPE/modifications`}
          refreshModifications={refresh}
        />
      ),
    },
    combinedRemoteData,
  )
}

export const BarcodeInstallationTableWrapper = ({
  config,
  barcode,
  common,
  override,
  installationId,
  refresh,
}: BarcodeInstallationTableProps) => {
  const combinedRemoteData = SRD.map2((barcode, common) => ({ barcode, common }), barcode, common)
  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div>
          <div className="alert alert-warning mb-md mt-md">
            No file seems to be uploaded to file stream
            <div>{err.body?.title}</div>
          </div>
        </div>
      ),
      success: ({ barcode, common }) => (
        <BarcodeInstallationTable
          config={config}
          database={barcode}
          commonModifications={common}
          installationModifications={override && SRD.isSuccess(override) ? override.data : []}
          modificationUrl={`${API_HOST}/v1/installation/${installationId}/SHAPE/modifications`}
          refreshModifications={refresh}
        />
      ),
    },
    combinedRemoteData,
  )
}

type CrateInstallationTableProps = {
  config: Config
  crate: RD<HttpError, CrateFile>
  common: RD<HttpError, Modification[]>
  override?: RD<HttpError, Modification[]>
  installationId?: string
  refresh: () => void
}

type CrateCommonTableProps = {
  config: Config
  crate: RD<HttpError, CrateFile>
  common: RD<HttpError, Modification[]>
  commonId: string
  refresh: () => void
}

export const CrateCommonTableWrapper = ({ config, crate, common, commonId, refresh }: CrateCommonTableProps) => {
  const combinedRemoteData = SRD.map2((crate, common) => ({ crate, common }), crate, common)
  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div>
          <div className="alert alert-warning mb-md mt-md">
            No file seems to be uploaded to file stream
            <div>{err.body?.title}</div>
          </div>
        </div>
      ),
      success: ({ common, crate }) => (
        <CrateCommonTable
          config={config}
          database={crate}
          commonModifications={common}
          modificationUrl={`${API_HOST}/v1/common/${commonId}/CRATE/modifications`}
          refreshModifications={refresh}
        />
      ),
    },
    combinedRemoteData,
  )
}

export const CrateInstallationTableWrapper = ({
  config,
  crate,
  common,
  override,
  installationId,
  refresh,
}: CrateInstallationTableProps) => {
  const combinedRemoteData = SRD.map2((crate, common) => ({ crate, common }), crate, common)
  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div>
          <div className="alert alert-warning mb-md mt-md">
            No file seems to be uploaded to file stream
            <div>{err.body?.title}</div>
          </div>
        </div>
      ),
      success: ({ common, crate }) => (
        <CrateInstallationTable
          config={config}
          database={crate}
          commonModifications={common}
          installationModifications={override && SRD.isSuccess(override) ? override.data : []}
          modificationUrl={`${API_HOST}/v1/installation/${installationId}/CRATE/modifications`}
          refreshModifications={refresh}
        />
      ),
    },
    combinedRemoteData,
  )
}

type ArticleDataTableProps = {
  artikeldaten: RD<HttpError, ArtikeldatenFile>
}

export const ArticleDataTableWrapper = ({ artikeldaten }: ArticleDataTableProps) => {
  return SRD.match(
    {
      notAsked: () => <div>Empty</div>,
      loading: () => <LoadingSpinner />,
      failure: (err) => (
        <div>
          <div className="alert alert-warning mb-md mt-md">
            No file seems to be uploaded to file stream
            <div>{err.body?.title}</div>
          </div>
        </div>
      ),
      success: (artikeldaten) => <ArtikeldatenTable database={artikeldaten} />,
    },
    artikeldaten,
  )
}
